import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingButton from "@enerex/unified-ui-react/LoadingButton";
import Table from "@enerex/unified-ui-react/Table";
import useShowSnackbar from "hooks/useShowSnackbar";

import { createAssociationColumns } from "pages/rateClass/list/associationColumns";
import AddSupplierRateClassModal from "./AddSupplierRateClassModal";
import DeleteRateClassAssociationModal from "pages/rateClass/list/DeleteRateClassAssociationModal";
import { deleteSupplierRateClass, getSupplierRateClassesBySupplier } from "pages/rateClass/list/rateClassApi";

export const SupplierRateClasses = ({ companyId, company }) => {
  const snackbar = useShowSnackbar();
  const snackbarRef = useRef(snackbar);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    snackbarRef.current = snackbar;
  }, [snackbar]);

  const loadSupplierRateClasses = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      setRows(await getSupplierRateClassesBySupplier(companyId));
    } catch (error) {
      setRows([]);
      snackbarRef.current(error.message || "Failed to load supplier rate classes", "error");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadSupplierRateClasses();
  }, [loadSupplierRateClasses]);

  const handleDelete = async () => {
    const supplierRateClassId = deleteModal.item?.supplierRateClassId;

    if (!supplierRateClassId) {
      snackbar("Unable to delete supplier rate class: missing record id", "error");
      return;
    }

    setDeleting(true);
    try {
      await deleteSupplierRateClass(supplierRateClassId);
      snackbar("Supplier rate class deleted", "success");
      setDeleteModal({ show: false, item: null });
      await loadSupplierRateClasses();
    } catch (error) {
      snackbar(error.message || "Failed to delete supplier rate class", "error");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => createAssociationColumns({
      nameHeader: "Utility",
      masterHeader: "Rate Class",
      aliasHeader: "Supplier Rate Class",
      showAliasColumn: true,
      showMasterColumn: true,
      onDelete: (item) => setDeleteModal({ show: true, item }),
    }),
    []
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
        <h4 className="header-title mb-0">Rate Classes</h4>
        <LoadingButton
          variant="primary"
          size="sm"
          className="fs-10"
          style={{
            padding: "2px 6px",
            fontSize: "10px",
            lineHeight: "1.2",
            display: "inline-flex",
            alignItems: "center",
            gap: "3px",
          }}
          onClick={() => setShowAddModal(true)}
        >
          <i className="uil uil-plus" style={{ fontSize: "9px" }} />
          <small>Add Rate Class</small>
        </LoadingButton>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <Table
            id="company-supplier-rate-classes"
            columns={columns}
            data={rows}
            loading={loading}
            total={rows.length}
            enableSearch={false}
            enablePagination={rows.length>=10}
            pageSizes={[10,25,50]}
            enableHideColumns={false}
            enableSorting={true}
            enableFilters={false}
            enableSelection={false}
            enablePaginateAll={false}
            enableColumnVisibilityStorage={false}
            enableGlobalFilterStorage={false}
            enableUrlPagination={false}
            noDataMessage="No Supplier Rate Classes found"
          />
        </div>
      </div>

      <DeleteRateClassAssociationModal
        show={deleteModal.show}
        type="supplier"
        item={deleteModal.item}
        deleting={deleting}
        onCancel={() => {
          if (!deleting) setDeleteModal({ show: false, item: null });
        }}
        onConfirm={handleDelete}
      />

      <AddSupplierRateClassModal
        show={showAddModal}
        company={company}
        onHide={() => setShowAddModal(false)}
        onSaved={async () => {
          snackbar("Supplier rate class added", "success");
          await loadSupplierRateClasses();
        }}
      />
    </>
  );
};
